import { v4 as uuidv4 } from 'uuid';
import { attractLoop } from '@/services/attract/attract-loop';
import { localSlot, utcSlot } from '@/services/attract/experiment';
import { clientEvents } from './client-events';
import {
  analyticsEnvironment,
  captureKioskAnalytics,
  isPostHogEnabled,
  POSTHOG_RETRY_DELAY_MS,
} from './posthog';

export type KioskAnalyticsEvent =
  | 'attract_shown'
  | 'attract_start_tap'
  | 'attract_variant_switched'
  | 'attract_video_failed'
  | 'attract_video_ready'
  | 'attract_video_started'
  | 'kiosk_heartbeat'
  | 'portrait_printed'
  | 'session_started'
  | 'visit_started'
  | 'visit_resumed'
  | 'visit_ended'
  | 'visit_interrupted'
  | 'session_start_failed'
  | 'step_viewed'
  | 'photo_received'
  | 'photo_failed'
  | 'faces_confirmed'
  | 'style_selected'
  | 'generation_started'
  | 'generation_failed'
  | 'consent_result'
  | 'voucher_validation'
  | 'portrait_result_shown'
  | 'portrait_selection_changed'
  | 'portrait_retry_requested'
  | 'order_confirmed'
  | 'payment_started'
  | 'payment_result'
  | 'delivery_result';

export type KioskAnalyticsValue = string | number | boolean | null;
export type KioskAnalyticsProperties = Record<
  string,
  KioskAnalyticsValue | undefined
>;

export const KIOSK_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
export const KIOSK_ANALYTICS_RETRY_DELAY_MS = POSTHOG_RETRY_DELAY_MS;

const MACHINE_ID_STORAGE_KEY = 'kiosk_analytics_machine_id';

// PostHog is a third party: the kiosk URL carries ?api_key=, session IDs
// embed the customer download code, and video URLs are not ours to share.
const FORBIDDEN_PROPERTY_KEY =
  /api_?key|secret|token|password|session_id|(download|gophoto|ticket|session)_code|url/i;
const URL_LIKE_VALUE = /^(https?:|blob:|data:)/i;
let heartbeat: ReturnType<typeof setInterval> | undefined;
let machineId: string | undefined;
let currentVisitId: string | null = null;

export function setKioskVisitId(id: string | null): void {
  currentVisitId = id;
}

export function isKioskAnalyticsEnabled(): boolean {
  return isPostHogEnabled();
}

function warn(message: string): void {
  // biome-ignore lint/suspicious/noConsole: analytics failures must stay visible but never throw.
  console.warn(message);
}

function readMachineId(): string {
  if (machineId) {
    return machineId;
  }
  try {
    machineId = localStorage.getItem(MACHINE_ID_STORAGE_KEY) ?? undefined;
    if (!machineId) {
      machineId = uuidv4();
      localStorage.setItem(MACHINE_ID_STORAGE_KEY, machineId);
    }
  } catch {
    machineId ??= uuidv4();
  }
  return machineId;
}

function readOrientation(): 'portrait' | 'landscape' | null {
  try {
    return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
  } catch {
    return null;
  }
}

function sanitize(
  properties: KioskAnalyticsProperties
): KioskAnalyticsProperties {
  const sanitized: KioskAnalyticsProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || FORBIDDEN_PROPERTY_KEY.test(key)) {
      continue;
    }
    if (typeof value === 'string' && URL_LIKE_VALUE.test(value.trim())) {
      continue;
    }
    sanitized[key] = value;
  }
  return sanitized;
}

export function buildKioskEventProperties(
  properties: KioskAnalyticsProperties = {},
  now: Date = new Date(),
  visitId: string | null = currentVisitId
): Record<string, KioskAnalyticsValue> {
  const context = clientEvents.getContext();
  const assignment = attractLoop.current.value;
  const id = readMachineId();
  return {
    ...(sanitize(properties) as Record<string, KioskAnalyticsValue>),
    app: 'b2c-kiosk',
    environment: analyticsEnvironment(),
    kiosk_slug: context.kiosk_slug ?? null,
    kiosk_mode: context.kiosk_mode ?? null,
    is_test: context.is_test === true,
    schema_version: 2,
    visit_id: visitId,
    experiment_key: assignment?.experimentKey ?? null,
    variant: assignment?.variant ?? null,
    slot: assignment?.slot ?? null,
    utc_slot: utcSlot(now).slot,
    local_slot: localSlot(now).slot,
    tz_offset_min: -now.getTimezoneOffset(),
    build_version: context.build_version ?? null,
    orientation: readOrientation(),
    distinct_id: visitId ? `kiosk-visit:${visitId}` : `kiosk-machine:${id}`,
    $process_person_profile: false,
  };
}

export function captureKioskEvent(
  event: KioskAnalyticsEvent,
  properties: KioskAnalyticsProperties = {},
  visitId: string | null = currentVisitId
): void {
  if (!isKioskAnalyticsEnabled()) {
    return;
  }
  try {
    const timestamp = new Date();
    const payload = buildKioskEventProperties(properties, timestamp, visitId);
    captureKioskAnalytics(event, payload, timestamp, visitId);
  } catch {
    warn(`[KioskAnalytics] Failed to capture ${event}`);
  }
}

function heartbeatProperties(): KioskAnalyticsProperties {
  try {
    return {
      step: clientEvents.getContext().step,
      online: navigator.onLine,
      visibility: document.visibilityState,
    };
  } catch {
    return {};
  }
}

export function startKioskHeartbeat(isKioskPage: () => boolean): void {
  if (heartbeat !== undefined || !isKioskAnalyticsEnabled()) {
    return;
  }
  heartbeat = setInterval(() => {
    if (isKioskPage()) {
      captureKioskEvent('kiosk_heartbeat', heartbeatProperties(), null);
    }
  }, KIOSK_HEARTBEAT_INTERVAL_MS);
}
